import type { MyFeedScope } from '@pulse/shared';
import { useQuery } from '@tanstack/react-query';

import {
  fetchMyDependencies,
  fetchMyEscalations,
  fetchMyIssues,
  fetchMyLedProjects,
  fetchMyRisks,
  fetchMyTimeSummary,
} from '../api/home.api';

// The four work-item keys are the *prefix* of the real query key — each hook appends its scope,
// so Individual and Lead cache side by side and toggling back is instant. Invalidating by the
// prefix still clears both, which is what HomePage's mutation handlers want: a status change
// made in one view must not leave the other stale.
export const HOME_KEYS = {
  issues: ['me', 'issues'] as const,
  risks: ['me', 'risks'] as const,
  dependencies: ['me', 'dependencies'] as const,
  escalations: ['me', 'escalations'] as const,
  timeSummary: ['me', 'time-summary'] as const,
  ledProjects: ['me', 'led-projects'] as const,
};

// `enabled` exists so HomePage can hold all four back until useMyLedProjects has resolved: the
// default view depends on whether the caller is a PL/PM, and starting these on `mine` only to
// re-run them on `led` a moment later would both double the requests and visibly flip the page.
export function useMyIssues(scope: MyFeedScope = 'mine', enabled = true) {
  return useQuery({ queryKey: [...HOME_KEYS.issues, scope], queryFn: () => fetchMyIssues(scope), enabled });
}

// Unconditional (once enabled), same as useMyIssues — the KPI row's Open Risks/Dependencies/
// Escalations cards need every kind's data on first load, not just whichever work-panel tab is
// active. React Query caches the result, so switching tabs afterward is still instant.
export function useMyRisks(scope: MyFeedScope = 'mine', enabled = true) {
  return useQuery({ queryKey: [...HOME_KEYS.risks, scope], queryFn: () => fetchMyRisks(scope), enabled });
}

export function useMyDependencies(scope: MyFeedScope = 'mine', enabled = true) {
  return useQuery({ queryKey: [...HOME_KEYS.dependencies, scope], queryFn: () => fetchMyDependencies(scope), enabled });
}

export function useMyEscalations(scope: MyFeedScope = 'mine', enabled = true) {
  return useQuery({ queryKey: [...HOME_KEYS.escalations, scope], queryFn: () => fetchMyEscalations(scope), enabled });
}

export function useMyTimeSummary() {
  return useQuery({ queryKey: HOME_KEYS.timeSummary, queryFn: fetchMyTimeSummary });
}

// Empty for anyone who is neither a Project Lead nor a Project Manager — which is exactly how
// HomePage decides whether to offer the Project/Individual toggle at all (§SA-409 3d).
export function useMyLedProjects() {
  return useQuery({ queryKey: HOME_KEYS.ledProjects, queryFn: fetchMyLedProjects });
}
